import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, change, getFormValues } from 'redux-form';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import messages from '../../../text/messages';
import { required } from './../../../utilities/validation';
import { SelectField } from './../../../components/fields';
import Modal from './../../../components/modal';
import WizardButtons from '../../../components/wizard/navigator/buttons';
import InviteForm from './../../../components/forms/invite';
import { getBanks, updateBankRecord, createBankRecord } from '../../../reducers/banking';
import { getUsers, usersAreLoaded } from '../../../reducers/users';
import { sendInvite } from '../../../reducers/invites';
import { RoleDefinitions } from '../../../utilities/roles';
import { updateUserDetails } from '../../../reducers/identity';
import { getAdmin } from '../../../reducers/onboarding';
import { ConfirmationDialog } from '../../../components/dialogs';
import { Snackbar } from '../../../components/snackbar';
import { anythingChanged } from '../../../utilities/common';
import Bank from '../../../components/forms/bank';

class Billing extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            modal: { open: false, values: undefined, type: undefined }
        };

        this.submit = this.submit.bind(this);

        // modal controls
        this.onOpen = this.onOpen.bind(this);
        this.onClose = this.onClose.bind(this);
    }

    componentDidMount() {
        let { role, admin, banks, client, users, usersRole, getBanks, getUsers, getAdmin } = this.props;
        if (!banks) getBanks(role);
        if (!users || !usersAreLoaded(role, usersRole)) getUsers(role);
        if (!admin) getAdmin(client.admin_id);
    }

    onOpen(values, type) {
        this.setState({ modal: { open: true, values, type } });
    }

    onClose() {
        this.setState({ modal: { open: false, values: undefined, type: undefined } });
    }

    submit(values, action) {
        const { role, banks, client, createBankRecord, updateBankRecord, getBanks, updateUserDetails } = this.props;
        const { bank } = values;

        let existingBank = banks && banks.length > 0 ? banks[0] : undefined;
        if (
            action &&
            existingBank &&
            existingBank.id === bank.id &&
            existingBank.name === bank.name &&
            existingBank.routing === bank.routing &&
            existingBank.account === bank.account
        ) {
            action();
            return;
        }

        updateUserDetails(role, { ...client, ap_user: values.ap_user }, () => {
            if (bank.id === undefined) {
                createBankRecord(bank, role, () => getBanks(role, action));
            } else {
                updateBankRecord(bank, role, () => getBanks(role, action));
            }
        });
    }

    render() {
        const { modal } = this.state;
        const { handleSubmit, submitting, role, banks, admin, client, changeField, sendInvite, getUsers, users, isPristine } = this.props;
        if (!admin || !banks || !users) {
            return null;
        }

        return (
            <div>
                <form onSubmit={handleSubmit(values => this.onOpen(values, 'submit'))}>
                    <div className="row section">
                        <div className="col-xs-12">
                            <FormattedHTMLMessage
                                id={'clientOnboarding.billingIntroText'}
                                defaultMessage={
                                    "<p>To pay your workers promptly, {admin} will debit the approved amount from {client}'s bank account automatically ahead of each payroll, just as other payroll services do.</p><p>Update your banking information below. {admin} will conduct an ACH check to confirm we can transact directly.</p>"
                                }
                                values={{ admin: admin.name, client: client.name }}
                                tagName={'div'}
                            />
                        </div>
                    </div>
                    <Bank
                        fieldsOptions={{
                            bankName: { name: 'bank.name' },
                            bankRouting: { name: 'bank.routing', size: 'col-xs-12 col-sm-4' },
                            bankAccount: { name: 'bank.account' },
                            size: 'col-xs-12 col-sm-4'
                        }}
                    />
                    <div className="row">
                        <div className="col-xs-12 col-sm-8">
                            <div className="row">
                                <div className="col-xs-9 col-sm-10">
                                    <Field
                                        name="ap_user"
                                        component={SelectField}
                                        options={users.map(user => ({ value: user.id, label: `${user.first_name} ${user.last_name}` }))}
                                        placeholder={messages.apContact}
                                        validate={required}
                                    />
                                </div>
                                <div className="col-xs-3 col-sm-2">
                                    <button type="button" className="field-inline-button" onClick={this.onOpen}>
                                        <FormattedMessage {...messages.add} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <WizardButtons disabled={submitting || isPristine} submit={{ message: <FormattedMessage {...messages.update} /> }} />
                </form>
                {modal.type === 'submit' ? (
                    <ConfirmationDialog
                        modal={modal}
                        message={<FormattedMessage {...messages.updatingConfirmation} values={{ entity: 'billing information' }} />}
                        onClose={this.onClose}
                        onSubmit={() => {
                            this.submit(modal.values, () => {
                                this.onClose();
                                Snackbar.success(<FormattedMessage {...messages.companyInfoUpdated} />);
                            });
                        }}
                    />
                ) : (
                    <Modal
                        open={modal.open}
                        onClose={this.onClose}
                        managedClose
                        size="col-sm-8 col-sm-offset-2 col-xs-12"
                        title={<FormattedMessage {...messages.addUser} />}
                    >
                        <div className="row">
                            <InviteForm
                                onSubmit={values =>
                                    sendInvite({ ...values, clientId: client.id }, role, () =>
                                        getUsers(role, result => {
                                            let newUser = result.find(u => u.email === values.email);
                                            changeField('client-company-billing', 'ap_user', newUser.id);
                                            this.onClose();
                                        })
                                    )
                                }
                                cancel={this.onClose}
                                tenantType={'client'}
                                initialValues={{ ref_id: role.id, ref_type: role.type, company_name: client.name }}
                                availableRoles={[RoleDefinitions.Client]}
                            />
                        </div>
                    </Modal>
                )}
            </div>
        );
    }
}

Billing = reduxForm({
    form: 'client-company-billing',
    destroyOnUnmount: false,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true,
    forceUnregisterOnUnmount: false
})(Billing);
const mapStateToProps = state => {
    const getBankFields = bank => {
        if (!bank) return bank;
        const { id, ref_id, ref_type, primary, name, routing, account } = bank;
        return { id, ref_id, ref_type, primary, name, routing, account };
    };

    let banks = state.banking.banks;
    let client = state.identity.details;
    let initialValues = {
        bank: banks && banks.length > 0 ? getBankFields(banks[0]) : undefined,
        ap_user: client.ap_user
    };
    const formValues = getFormValues('client-company-billing')(state);
    if (formValues && formValues.bank) {
        formValues.bank = getBankFields(formValues.bank);
    }
    const isPristine = anythingChanged({ initialValues, values: formValues });

    return {
        role: state.identity.role,
        admin: state.onboarding.admin,
        client,
        banks,
        users: state.users.list,
        usersRole: state.users.listRole,
        initialValues,
        isPristine
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            changeField: change,
            getAdmin: getAdmin,
            getBanks: getBanks,
            createBankRecord: createBankRecord,
            updateBankRecord: updateBankRecord,
            getUsers: getUsers,
            sendInvite: sendInvite,
            updateUserDetails: updateUserDetails
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(Billing);



// WEBPACK FOOTER //
// ./src/pages/company/forms/billing.jsx